import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ChevronDown, FileText } from 'lucide-react';
import PageHero from '../../components/PageHero';
import CTASection from '../../components/CTASection';
import { CASE_STUDIES_DETAILED } from '../../config/siteContent';
import useDocumentTitle from '../../hooks/useDocumentTitle';

/**
 * CaseStudiesIndex - single-page accordion (May 2026 redesign).
 *
 * Why a single page?
 *   - Each case study is short (5 fields), no need for a dedicated route
 *   - Users can browse + skim multiple stories without navigating
 *   - Industry filter chips stay useful at the top
 *   - Deep-linking via clean URL: `/case-studies/strattec` auto-opens that
 *     accordion item and scrolls to it. Legacy `#strattec` hash fragments
 *     still work for backwards compat.
 *
 * Replaces: the previous `<Bento grid>` + `<CaseStudyDetail>` per-slug pages.
 * Old `/case-studies/:slug` routes redirect here via React Router with the
 * slug as an anchor hash.
 */
const CaseStudiesIndex = () => {
  useDocumentTitle({
    title: 'Case Studies',
    description:
      'See how iConnect powers EDI for STRATTEC, INOAC, J.L. French, Stryker, Webasto-Edscha Cabrio, FTE Automotive, National Engineered Fasteners, Arnecom, and Continental.',
  });

  const [filter, setFilter] = useState('All');
  const location = useLocation();
  const { slug: routeSlug } = useParams();

  // Compute the initial open set synchronously so a route-slug accordion
  // mounts already open. (AnimatePresence + `initial={false}` leaves content
  // at height: 0 if the open state is set via a post-mount useEffect.)
  const [openIds, setOpenIds] = useState(() => {
    const slug = routeSlug || (location.hash || '').replace('#', '');
    return slug && CASE_STUDIES_DETAILED.some((c) => c.id === slug)
      ? new Set([slug])
      : new Set();
  });

  // Industry filter list (derived from data so adding case studies works)
  const industries = useMemo(
    () => ['All', ...Array.from(new Set(CASE_STUDIES_DETAILED.map((c) => c.industry)))],
    []
  );

  const visible = useMemo(() => {
    if (filter === 'All') return CASE_STUDIES_DETAILED;
    return CASE_STUDIES_DETAILED.filter((c) => c.industry === filter);
  }, [filter]);

  // Handle client-side navigation between slugs after mount.
  useEffect(() => {
    const id = routeSlug || location.hash.replace('#', '');
    if (!id) return;
    const target = CASE_STUDIES_DETAILED.find((c) => c.id === id);
    if (!target) return;
    setOpenIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 60);
    return () => clearTimeout(t);
  }, [routeSlug, location.hash]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <PageHero
        eyebrow="Case Studies"
        title="Real customers."
        titleAccent="Real outcomes."
        breadcrumbs={[{ label: 'Case Studies' }]}
        subhead="From global automotive OEMs to medical-device manufacturers and industrial suppliers - iConnect powers the EDI behind some of the most demanding supply chains."
      />

      <section className="py-16 lg:py-20 bg-bg-canvas">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {/* Industry filter chips */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {industries.map((ind) => {
              const active = filter === ind;
              return (
                <button
                  key={ind}
                  onClick={() => setFilter(ind)}
                  className={`px-4 py-2.5 min-h-[40px] rounded-full text-sm font-mono uppercase tracking-wider font-bold transition-all ${
                    active
                      ? 'bg-gradient-sky text-white shadow-glow-sky'
                      : 'bg-bg-soft text-text-secondary border border-border-subtle hover:border-accent-primary/40 hover:text-accent-primary'
                  }`}
                >
                  {ind}
                </button>
              );
            })}
          </div>

          {/* Accordion list */}
          <div className="space-y-3">
            {visible.map((study) => {
              const isOpen = openIds.has(study.id);
              return (
                <div
                  key={study.id}
                  id={study.id}
                  className="card-elevated overflow-hidden scroll-mt-24"
                >
                  <button
                    onClick={() => toggle(study.id)}
                    className="w-full px-6 py-5 lg:px-8 lg:py-6 flex items-center justify-between gap-4 text-left group"
                    aria-expanded={isOpen}
                  >
                    {study.logo && (
                      <div className="hidden sm:flex w-14 h-14 rounded-lg bg-white border border-border-subtle items-center justify-center flex-shrink-0 p-2">
                        <img
                          src={study.logo}
                          alt={`${study.name} logo`}
                          loading="lazy"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent-deep font-bold mb-1">
                        {study.industry}
                      </div>
                      <h3 className="font-display font-extrabold text-xl lg:text-2xl text-text-primary tracking-tight">
                        {study.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="hidden sm:block text-right">
                        <div className="font-display font-extrabold text-2xl text-gradient-sky leading-none">
                          {study.metric}
                        </div>
                        <div className="text-[10px] text-text-muted uppercase tracking-wider">
                          {study.metricLabel}
                        </div>
                      </div>
                      <div
                        className={`w-9 h-9 rounded-full bg-bg-soft border border-border-subtle flex items-center justify-center transition-transform group-hover:border-accent-primary/40 ${
                          isOpen ? 'rotate-180 bg-accent-soft border-accent-primary/40' : ''
                        }`}
                      >
                        <ChevronDown
                          size={18}
                          className={isOpen ? 'text-accent-primary' : 'text-text-muted'}
                        />
                      </div>
                    </div>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 lg:px-8 lg:pb-8 border-t border-border-subtle pt-5">
                        <p className="text-sm text-text-secondary leading-relaxed mb-5 italic">
                          {study.companyDescription}
                        </p>

                        <div className="grid sm:grid-cols-3 gap-4 mb-5">
                          <Block label="Challenge" body={study.challenge} />
                          <Block label="Solution" body={study.solution} />
                          <Block label="Outcome" body={study.outcome} />
                        </div>

                        {study.pdf && (
                          <a
                            href={study.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-primary hover:text-accent-bright transition-colors"
                          >
                            <FileText size={14} />
                            Read full case study (PDF)
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {visible.length === 0 && (
            <p className="text-center text-text-muted mt-12">
              No case studies match this filter.
            </p>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
};

/** A single block in the Challenge / Solution / Outcome row. */
const Block = ({ label, body }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent-primary font-bold mb-1.5">
      {label}
    </div>
    <p className="text-sm text-text-primary leading-relaxed">{body}</p>
  </div>
);

export default CaseStudiesIndex;
